// Frontend/src/components/admin/forms/CellGroupForm.jsx
import { useEffect, useState } from 'react';
import Input from '../ui/Input';
import Select from '../ui/Select';

const CellGroupForm = ({ 
  initialValues, 
  onSubmit, 
  onCancel,
  campuses = [],
  showCampusField = true,
  defaultCampusId = null
}) => {
  const [formData, setFormData] = useState({
    name: '',
    campus: null,
    location: '',
    meetingDay: '',
    meetingTime: '',
    meetingLocation: '',
    groupType: 'general',
    contactEmail: '',
    contactPhone: '',
    ...initialValues
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set default campus if provided and no initial values
  useEffect(() => {
    if (defaultCampusId && !initialValues?.campus) {
      setFormData(prev => ({
        ...prev,
        campus: defaultCampusId
      }));
    }
  }, [defaultCampusId, initialValues]);

  // Update form when initialValues change
  useEffect(() => {
    if (initialValues) {
      setFormData(prev => ({
        ...prev,
        ...initialValues,
        // Ensure meeting fields are properly initialized
        meetingDay: initialValues.meeting_day || initialValues.meetingDay || '',
        meetingTime: initialValues.meeting_time || initialValues.meetingTime || '',
        meetingLocation: initialValues.meeting_location || initialValues.meetingLocation || ''
      }));
    }
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Format the data before submitting
      const submissionData = {
        ...formData,
        meeting_day: formData.meetingDay,
        meeting_time: formData.meetingTime,
        meeting_location: formData.meetingLocation,
        // Remove the temporary fields
        meetingDay: undefined,
        meetingTime: undefined,
        meetingLocation: undefined
      };
      await onSubmit(submissionData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Cell Group Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        required
      />
      
      <Select
          label="Campus"
          name="campus"
          value={formData.campus || ''}
          onChange={handleChange}
          required
          options={[
            { value: '', label: 'Select a campus', disabled: true },
            ...campuses.map(campus => ({
              value: campus.id,
              label: campus.name
            }))
          ]}
        />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          label="Meeting Day"
          name="meetingDay"
          value={formData.meetingDay}
          onChange={handleChange}
          options={[
            { value: '', label: 'Select day', disabled: true },
            { value: 'Monday', label: 'Monday' },
            { value: 'Tuesday', label: 'Tuesday' },
            { value: 'Wednesday', label: 'Wednesday' },
            { value: 'Thursday', label: 'Thursday' },
            { value: 'Friday', label: 'Friday' },
          ]}
        />

        <Input
          label="Meeting Time"
          name="meetingTime"
          type="time"
          value={formData.meetingTime}
          onChange={handleChange}
        />

        <Input
          label="Meeting Location"
          name="meetingLocation"
          value={formData.meetingLocation}
          onChange={handleChange}
          placeholder="Physical address or online meeting link"
        />
      </div>
      
      <Select
        label="Group Type"
        name="groupType"
        value={formData.groupType}
        onChange={handleChange}
        options={[
          { value: 'general', label: 'General' },
          { value: 'youth', label: 'Youth' },
          { value: 'women', label: 'Women' },
          { value: 'men', label: 'Men' },
          { value: 'children', label: 'Children' },
          { value: 'seniors', label: 'Seniors' }
        ]}
      />
      
      <Input
        label="Contact Email"
        name="contactEmail"
        type="email"
        value={formData.contactEmail}
        onChange={handleChange}
      />
      
      <Input
        label="Contact Phone"
        name="contactPhone"
        value={formData.contactPhone}
        onChange={handleChange}
      />
      
      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          className="btn btn-outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Cell Group'}
        </button>
      </div>
    </form>
  );
};

export default CellGroupForm;